"use client";

import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import ShadowCard from "@/components/common/ShadowCard";
import Image from "next/image";

interface GameCardSectionProps {
  subtitle: string;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  reversed?: boolean;
}

export default function GameCardSection({
  subtitle,
  title,
  description,
  imageSrc,
  imageAlt,
  reversed = false,
}: GameCardSectionProps) {
  const [animationDistance, setAnimationDistance] = useState({
    gradient: 200,
    shadow: 150,
  });

  useEffect(() => {
    const updateDistance = () => {
      if (window.innerWidth >= 1024) {
        setAnimationDistance({ gradient: 800, shadow: 400 });
      } else {
        setAnimationDistance({ gradient: 200, shadow: 150 });
      }
    };
    updateDistance();
    window.addEventListener("resize", updateDistance);
    return () => window.removeEventListener("resize", updateDistance);
  }, []);

  // Responsive animation distances
  const gradientXDistance = animationDistance.gradient;
  const shadowXDistance = animationDistance.shadow;

  // Text animation variants - comes from bottom to top
  const textVariants = {
    hidden: {
      y: 80,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
        delay: 0.1,
      },
    },
  };

  // Card animation variants
  const gradientCardVariants = {
    hidden: {
      x: reversed ? gradientXDistance : -gradientXDistance,
      opacity: 0,
      rotate: -18,
    },
    visible: {
      x: 0,
      opacity: 1,
      rotate: -18,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1] as const,
        delay: 0,
      },
    },
  };

  const shadowCardVariants = {
    hidden: {
      x: reversed ? shadowXDistance : -shadowXDistance,
      opacity: 0,
      rotate: -10.36,
    },
    visible: {
      x: 0,
      opacity: 1,
      rotate: -10.36,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1] as const,
        delay: 0.2,
      },
    },
  };

  // Content component with animation
  const TextContent = (
    <motion.div
      className="flex flex-col justify-center lg:max-w-[500px]"
      variants={textVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <p className="font-raleway mb-2.5 text-base font-normal text-gray-400">
        {subtitle}
      </p>
      <h2 className="font-raleway mb-2.5 text-xl leading-tight font-bold text-white md:text-3xl md:text-[2rem]">
        {title}
      </h2>
      <p className="font-raleway text-base leading-[1.2] text-gray-300 md:text-base">
        {description}
      </p>
    </motion.div>
  );

  // Cards component - positioned to extend beyond viewport
  const CardsStack = (
    <div className="relative mt-16 mb-8 h-[318px] w-[265px] lg:my-0 lg:h-[722px] lg:w-[600px]">
      {/* Gradient Card (Background) */}
      <motion.div
        variants={gradientCardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className="absolute inset-0 rounded-[20px] bg-linear-to-br from-[#EE4137] to-[#A10900]"
        style={{
          width: "100%",
          height: "100%",
          transformOrigin: "center center",
        }}
      />

      {/* Shadow Card with Image (Foreground) */}
      <motion.div
        variants={shadowCardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className={`absolute inset-0 lg:inset-auto lg:top-[27px] ${reversed ? "lg:left-0" : "lg:left-[36px]"} lg:h-[calc(100%-27px)] lg:w-[calc(100%-36px)]`}
        style={{
          transformOrigin: "center center",
        }}
      >
        <ShadowCard className="h-full w-full overflow-hidden">
          <Image src={imageSrc} alt={imageAlt} fill className="object-cover" />
        </ShadowCard>
      </motion.div>
    </div>
  );

  return (
    <section className="bg-brand-black relative w-full overflow-hidden py-16 lg:py-24">
      <div
        className={`flex flex-col items-center gap-10 px-5 md:gap-20 md:px-12 lg:flex-row lg:items-center lg:gap-85 lg:px-0 ${
          reversed ? "lg:flex-row-reverse" : ""
        }`}
      >
        {/* Text Content */}
        <div
          className={`w-full lg:flex-1 ${reversed ? "lg:pr-[100px]" : "lg:pl-[100px]"}`}
        >
          {TextContent}
        </div>
        {/* Cards - extend to edge on desktop, centered on mobile */}
        <div className="flex w-full justify-center lg:w-auto lg:shrink-0 lg:justify-start">
          {CardsStack}
        </div>
      </div>
    </section>
  );
}
